import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { trackingService } from '@/hooks/useTracking';

interface TrackingProviderProps {
  children: ReactNode;
}

export function TrackingProvider({ children }: TrackingProviderProps) {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Log initialization
    console.log(
      '%c[TRACKING SYSTEM]',
      'background: linear-gradient(135deg, #3B6CFF, #7AA8FF); color: white; padding: 6px 12px; border-radius: 8px; font-weight: bold; font-size: 14px;'
    );
    console.log(
      '%cTracking is active! All clicks will be logged.',
      'color: #5A6A85;'
    );
    console.log(
      '%cUse %cwindow.tracking.printStats()%c to see click statistics.',
      'color: #5A6A85;',
      'color: #3B6CFF; font-weight: bold;',
      'color: #5A6A85;'
    );

    // Set up global click listener
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Find the clickable parent element
      let clickableElement = target;
      while (clickableElement && clickableElement !== document.body) {
        const tagName = clickableElement.tagName.toLowerCase();
        const isClickable = 
          tagName === 'button' ||
          tagName === 'a' ||
          tagName === 'input' ||
          tagName === 'select' ||
          clickableElement.getAttribute('role') === 'button' ||
          clickableElement.classList.contains('clickable');
        
        if (isClickable) {
          trackingService.trackClick(e as unknown as React.MouseEvent, clickableElement);
          break;
        }
        clickableElement = clickableElement.parentElement as HTMLElement;
      }
    };

    document.addEventListener('click', handleGlobalClick, true);

    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, []);

  // Track page changes
  useEffect(() => {
    console.log(
      '%c[PAGE VIEW]',
      'background: #10B981; color: white; padding: 3px 8px; border-radius: 4px; font-weight: bold;',
      {
        path: location.pathname,
        time: new Date().toLocaleTimeString(),
      }
    );
  }, [location.pathname]);

  return (
    <div ref={containerRef} className="tracking-container">
      {children}
    </div>
  );
}

